import { Transaction } from '@/types/transaction';

// Get the API key from environment variables
const OPENAI_API_KEY = import.meta.env.VITE_OPENAI_API_KEY;

// Log the API key status (without exposing the actual key)
console.log('OpenAI API Key status:', OPENAI_API_KEY ? 'Configured' : 'Not configured');

if (!OPENAI_API_KEY) {
  console.warn('OpenAI API key is not configured');
}

export async function analyzeReceipt(base64Image: string) {
  try {
    console.log('Making OpenAI API request...');
    console.log('');

    // First step: Extract all possible information from the receipt
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${OPENAI_API_KEY}`
      },
      body: JSON.stringify({
        model: "gpt-4-turbo",
        messages: [
          {
            role: "user",
            content: [
              {
                type: "text",
                text: "Extract all possible information from this receipt. Look for: total amount, date, store name, items purchased, prices, tax, payment method, and any other relevant details. Return the information in a structured format."
              },
              {
                type: "image_url",
                image_url: {
                  url: `data:image/jpeg;base64,${base64Image}`
                }
              }
            ]
          }
        ],
        max_tokens: 1000
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('OpenAI API Error:', errorData);
      throw new Error(`OpenAI API error: ${errorData.error?.message || 'Unknown error'}`);
    }

    const data = await response.json();
    console.log('OpenAI API Response:', data);

    if (!data.choices?.[0]?.message?.content) {
      throw new Error('No content in OpenAI API response');
    }

    // Second step: Process the extracted information into a transaction format
    const extractedInfo = data.choices[0].message.content;
    const processResponse = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${OPENAI_API_KEY}`
      },
      body: JSON.stringify({
        model: "gpt-4-0613",
        messages: [
          {
            role: "system",
            content: "You are a transaction formatter. Convert receipt information into a standardized transaction format. Return ONLY a JSON object with these fields: amount (number), date (YYYY-MM-DD), place (string), items (array of strings), category (string). Use your judgment to categorize the transaction and format the data appropriately."
          },
          {
            role: "user",
            content: extractedInfo
          }
        ],
        temperature: 0.3,
        max_tokens: 500
      })
    });

    if (!processResponse.ok) {
      throw new Error('Failed to process receipt information');
    }

    const processData = await processResponse.json();
    const content = processData.choices[0].message.content;
    
    let result;
    try {
      result = JSON.parse(content);
    } catch (parseError) {
      console.error('Error parsing JSON:', parseError);
      // If parsing fails, try to extract JSON from the content
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        result = JSON.parse(jsonMatch[0]);
      } else {
        throw new Error('Could not parse JSON from response');
      }
    }

    // Validate and format the result
    const formattedResult = {
      amount: Number(result.amount) || 0,
      date: result.date || new Date().toISOString().split('T')[0],
      place: result.place || '',
      items: Array.isArray(result.items) ? result.items : [],
      category: result.category || 'Other'
    };

    console.log('Formatted result:', formattedResult);
    return formattedResult;
  } catch (error) {
    console.error('Error analyzing receipt:', error);
    // Return a default transaction object that can be edited
    return {
      amount: 0,
      date: new Date().toISOString().split('T')[0],
      place: '',
      items: [],
      category: 'Other'
    };
  }
}

export async function transcribeAudio(audioBlob: Blob) {
  try {
    console.log('Starting audio transcription...');
    const formData = new FormData();
    formData.append('file', audioBlob, 'audio.wav');
    formData.append('model', 'whisper-1');

    const response = await fetch('https://api.openai.com/v1/audio/transcriptions', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${OPENAI_API_KEY}`
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('OpenAI API Error:', errorData);
      throw new Error(`OpenAI API error: ${errorData.error?.message || 'Unknown error'}`);
    }

    const data = await response.json();
    console.log('Transcription result:', data);
    return data.text;
  } catch (error) {
    console.error('Error transcribing audio:', error);
    throw error;
  }
}

export async function extractTransactionDetails(text: string) {
  try {
    console.log('Extracting transaction details...');
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${OPENAI_API_KEY}`
      },
      body: JSON.stringify({
        model: "gpt-4-0613",
        messages: [
          {
            role: "system",
            content: "You are a financial transaction analyzer. Extract transaction details from the given text and return ONLY a JSON object with this exact structure: {\"transactions\": [{\"amount\": number, \"category\": string, \"date\": \"YYYY-MM-DD\", \"description\": string, \"type\": \"income\" | \"expense\"}]}. Do not include any other text or explanation."
          },
          {
            role: "user",
            content: text
          }
        ],
        temperature: 0.3,
        max_tokens: 500
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('OpenAI API Error:', errorData);
      throw new Error(`OpenAI API error: ${errorData.error?.message || 'Unknown error'}`);
    }

    const data = await response.json();
    console.log('Transaction details result:', data);

    if (!data.choices?.[0]?.message?.content) {
      throw new Error('No content in OpenAI API response');
    }

    // Parse the content as JSON
    const content = data.choices[0].message.content;
    const result = JSON.parse(content);
    console.log('Parsed transaction details:', result);

    return result;
  } catch (error) {
    console.error('Error extracting transaction details:', error);
    throw error;
  }
}

export async function analyzeTransactions(transactions: Transaction[]) {
  try {
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${OPENAI_API_KEY}`,
      },
      body: JSON.stringify({
        model: "gpt-4",
        messages: [
          {
            role: "system",
            content: `You are a financial analyst. Analyze the given transactions and provide insights about:
            1. Spending patterns and trends
            2. Financial health assessment
            3. Specific recommendations for improvement
            
            Format the response as JSON with these fields:
            {
              "spendingPatterns": "string describing patterns",
              "financialHealth": "string assessing financial health",
              "recommendations": ["array of specific recommendations"]
            }
            
            Make the insights specific to the user's transaction history and provide actionable recommendations.`
          },
          {
            role: "user",
            content: JSON.stringify(transactions)
          }
        ],
        temperature: 0.7,
        max_tokens: 1000
      })
    });

    if (!response.ok) {
      throw new Error(`OpenAI API error: ${response.statusText}`);
    }

    const data = await response.json();
    const content = data.choices[0].message.content;
    const insights = JSON.parse(content);
    
    return {
      spendingPatterns: insights.spendingPatterns || 'No spending patterns detected.',
      financialHealth: insights.financialHealth || 'Unable to assess financial health.',
      recommendations: Array.isArray(insights.recommendations) ? insights.recommendations : ['No specific recommendations available.']
    };
  } catch (error) {
    console.error('Error analyzing transactions:', error);
    return {
      spendingPatterns: 'Unable to analyze spending patterns.',
      financialHealth: 'Unable to assess financial health.',
      recommendations: ['Unable to generate recommendations at this time.']
    };
  }
} 